// src/lib/scaleFactor.ts
/** 
 * Utilities for parsing and validating scale factors for T-12 Double Number Line
 */

export interface Fraction {
    n: number; // numerator
    d: number; // denominator
  }
  
  /**
   * Parse scale factor input from various formats
   * Accepts: integers (2), decimals (1.5), fractions (3/4)
   */
  export function parseScaleFactor(input: string): Fraction | null {
    const trimmed = input.trim();
    
    if (!trimmed) return null;
    
    // Handle fraction format (e.g., "3/4", "22/7")
    if (trimmed.includes('/')) {
      const parts = trimmed.split('/');
      if (parts.length !== 2) return null;
      
      const numerator = parseInt(parts[0].trim(), 10);
      const denominator = parseInt(parts[1].trim(), 10);
      
      if (isNaN(numerator) || isNaN(denominator) || denominator === 0) {
        return null;
      }
      
      return { n: numerator, d: denominator };
    }
    
    // Handle decimal format (e.g., "1.5", "0.25")
    if (trimmed.includes('.')) {
      const decimal = parseFloat(trimmed);
      if (isNaN(decimal)) return null;
      
      return decimalToFraction(decimal);
    }
    
    // Handle integer format (e.g., "2", "5")
    const integer = parseInt(trimmed, 10);
    if (isNaN(integer)) return null;
    
    return { n: integer, d: 1 };
  }
  
  /**
   * Convert decimal to fraction with reasonable denominator limit
   */
  export function decimalToFraction(decimal: number, maxDenominator: number = 1000): Fraction {
    if (Number.isInteger(decimal)) {
      return { n: decimal, d: 1 };
    }
    
    // Simple continued fraction approach
    let sign = decimal < 0 ? -1 : 1;
    decimal = Math.abs(decimal);
    
    let wholePart = Math.floor(decimal);
    let fractionalPart = decimal - wholePart;
    
    if (fractionalPart === 0) {
      return { n: sign * wholePart, d: 1 };
    }
    
    // Find best rational approximation
    let bestN = 0, bestD = 1;
    let minError = Math.abs(fractionalPart);
    
    for (let d = 1; d <= maxDenominator; d++) {
      let n = Math.round(fractionalPart * d);
      let error = Math.abs(fractionalPart - n / d);
      
      if (error < minError) {
        minError = error;
        bestN = n;
        bestD = d;
      }
      
      if (error < 1e-10) break; // Close enough
    }
    
    return { 
      n: sign * (wholePart * bestD + bestN), 
      d: bestD 
    };
  }
  
  /**
   * Convert fraction to decimal
   */
  export function fractionToDecimal(fraction: Fraction): number {
    return fraction.n / fraction.d;
  }
  
  /**
   * Format fraction for display
   */
  export function formatScaleFactor(fraction: Fraction, format: 'mixed' | 'improper' = 'improper'): string {
    if (fraction.d === 1) {
      return fraction.n.toString();
    }
    
    if (format === 'mixed' && Math.abs(fraction.n) >= fraction.d) {
      const whole = Math.floor(Math.abs(fraction.n) / fraction.d);
      const remainder = Math.abs(fraction.n) % fraction.d;
      const sign = fraction.n < 0 ? '-' : '';
      
      if (remainder === 0) {
        return `${sign}${whole}`;
      }
      
      return `${sign}${whole} ${remainder}/${fraction.d}`;
    }
    
    return `${fraction.n}/${fraction.d}`;
  }

  /**
   * Convert fraction to LaTeX format for KaTeX rendering
   */
  export function fractionToLatex(fraction: Fraction, format: 'mixed' | 'improper' = 'improper'): string {
    if (fraction.d === 1) {
      return fraction.n.toString();
    }
    
    if (format === 'mixed' && Math.abs(fraction.n) >= fraction.d) {
      const whole = Math.floor(Math.abs(fraction.n) / fraction.d);
      const remainder = Math.abs(fraction.n) % fraction.d;
      const sign = fraction.n < 0 ? '-' : '';
      
      if (remainder === 0) {
        return `${sign}${whole}`;
      }
      
      return `${sign}${whole}\\frac{${remainder}}{${fraction.d}}`;
    }
    
    return `\\frac{${fraction.n}}{${fraction.d}}`;
  }
  
  /**
   * Simplify fraction to lowest terms
   */
  export function simplifyFraction(fraction: Fraction): Fraction {
    const gcd = (a: number, b: number): number => {
      a = Math.abs(a);
      b = Math.abs(b);
      while (b !== 0) {
        [a, b] = [b, a % b];
      }
      return a;
    };
    
    const divisor = gcd(fraction.n, fraction.d);
    return {
      n: fraction.n / divisor,
      d: fraction.d / divisor
    };
  }
  
  /**
   * Validate scale factor (must be positive for basic usage)
   */
  export function validateScaleFactor(fraction: Fraction | null, allowNegative: boolean = false): string | null {
    if (!fraction) {
      return "Invalid scale factor format";
    }
    
    if (fraction.d === 0) {
      return "Denominator cannot be zero";
    }
    
    const decimal = fractionToDecimal(fraction);
    
    if (decimal === 0) {
      return "Scale factor cannot be zero";
    }
    
    if (!allowNegative && decimal < 0) {
      return "Scale factor must be positive";
    }
    
    if (Math.abs(decimal) > 1000) {
      return "Scale factor too large (max 1000)";
    }
    
    if (Math.abs(decimal) < 0.001) {
      return "Scale factor too small (min 0.001)";
    }
    
    return null; // Valid
  }
  
  /**
   * Apply scale factor to a value
   */
  export function applyScaleFactor(value: number, scaleFactor: Fraction): number {
    return value * fractionToDecimal(scaleFactor);
  }
  
  /**
   * Common scale factor presets
   */
  export const SCALE_FACTOR_PRESETS: Array<{ label: string; fraction: Fraction; description: string }> = [
    { label: "1/2", fraction: { n: 1, d: 2 }, description: "Half scale" },
    { label: "2", fraction: { n: 2, d: 1 }, description: "Double scale" },
    { label: "1/3", fraction: { n: 1, d: 3 }, description: "One third" },
    { label: "3", fraction: { n: 3, d: 1 }, description: "Triple scale" },
    { label: "1/4", fraction: { n: 1, d: 4 }, description: "Quarter scale" },
    { label: "3/4", fraction: { n: 3, d: 4 }, description: "Three quarters" },
    { label: "1.5", fraction: { n: 3, d: 2 }, description: "One and a half" },
    { label: "0.1", fraction: { n: 1, d: 10 }, description: "One tenth" },
  ];
  
  /**
   * Get appropriate decimal places for display
   */
  export function getDecimalPlaces(fraction: Fraction): number {
    const decimal = fractionToDecimal(fraction);
    const str = decimal.toString();
    const decimalIndex = str.indexOf('.');
    
    if (decimalIndex === -1) return 0;
    
    return Math.min(str.length - decimalIndex - 1, 6); // Max 6 decimal places
  }